import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle, Clock, ShoppingCart, CreditCard, AlertTriangle, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Card Element styling to match dark theme
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: false,
};

// Submit button component using proper Stripe Elements
const PaymentSubmitButton = ({ onApprove, onError, disabled, total, orderId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  // Map Stripe error codes to user-friendly messages
  const parseStripeError = (error) => {
    if (error?.code === 'card_declined') return 'Your card was declined. Please try a different payment method.';
    if (error?.code === 'expired_card') return 'Your card has expired. Please use a different card.';
    if (error?.code === 'incorrect_cvc') return 'Invalid security code (CVC). Please check and try again.';
    if (error?.code === 'insufficient_funds') return 'Insufficient funds. Please try a different payment method.';
    if (error?.message) return error.message;
    return 'Payment failed. Please try again.';
  };

  const handleClick = async () => {
    if (!stripe || !elements) {
      onError({ message: 'Payment system not ready. Please refresh the page.' });
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onError({ message: 'Card details not ready. Please try again.' });
      return;
    }

    setProcessing(true);
    try {
      // Create PaymentIntent on backend
      const intentResponse = await axios.post(`${API}/payments/create-intent`, {
        amount: total,
        description: `POS Order #${orderId}`,
        metadata: { pos_order_id: orderId }
      });

      if (!intentResponse.data.success) {
        throw new Error(intentResponse.data.error || 'Failed to create payment');
      }

      const { client_secret, payment_intent_id } = intentResponse.data;

      // Confirm the payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        console.error('Payment error:', error);
        onError({ message: parseStripeError(error) });
      } else if (paymentIntent.status === 'succeeded') {
        await onApprove({ 
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status 
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      onError({ message: err.message || 'Payment failed. Please try again.' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || processing || !stripe}
      className="w-full h-14 text-lg font-semibold bg-teal-600 hover:bg-teal-500 text-white"
      data-testid="pos-payment-submit"
    >
      {processing ? (
        <>
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Processing Payment...
        </>
      ) : (
        <>
          <Lock className="h-5 w-5 mr-2" />
          Pay ${total?.toFixed(2)}
        </>
      )}
    </Button>
  );
};

const POSPaymentPage = () => {
  const { orderId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [wooOrderId, setWooOrderId] = useState(null);

  useEffect(() => {
    fetchOrder();
    fetchPaymentConfig();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/pos/pending-orders/${orderId}`);
      setOrder(response.data.order);
      setExpired(response.data.expired);
      
      if (response.data.order?.status === 'completed') {
        setPaymentComplete(true);
        setWooOrderId(response.data.order.woo_order_id);
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.response?.data?.detail || 'Order not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/stripe/config`);
      setPaymentConfig(response.data);
      if (response.data?.publishable_key) {
        const stripe = loadStripe(response.data.publishable_key);
        setStripePromise(stripe);
      }
    } catch (err) {
      console.error('Error fetching payment config:', err);
    }
  };

  // This creates the Stripe order when hosted fields are submitted
  const createOrder = useCallback(async () => {
    try {
      console.log('Creating Stripe order for pending order:', orderId);
      const response = await axios.post(`${API}/pos/pending-orders/${orderId}/create-stripe-order`);
      console.log('Stripe order created:', response.data.stripe_order_id);
      return response.data.stripe_order_id;
    } catch (err) {
      console.error('Error creating Stripe order:', err);
      throw err;
    }
  }, [orderId]);

  // This is called after hosted fields submit successfully
  const onApprove = async (data) => {
    setPaymentProcessing(true);
    setError(null);
    try {
      console.log('Completing order with Stripe order ID:', data.orderID);
      const response = await axios.post(`${API}/pos/pending-orders/${orderId}/complete`, {
        stripe_order_id: data.orderID
      });
      
      setPaymentComplete(true);
      setWooOrderId(response.data.woo_order_id);
    } catch (err) {
      console.error('Payment completion error:', err);
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const onError = (err) => {
    console.error('Payment error:', err);
    setError(err.message || 'Payment failed. Please try again.');
    setPaymentProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span>Loading order...</span>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <XCircle className="h-16 w-16 text-red-500 mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Order Not Found</h2>
              <p className="text-slate-400">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Clock className="h-16 w-16 text-amber-500 mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Payment Link Expired</h2>
              <p className="text-slate-400">This payment link has expired. Please contact us for a new link.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <CheckCircle className="h-16 w-16 text-emerald-500 mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-slate-400 mb-4">
                Thank you for your payment. Your order has been completed.
              </p>
              {wooOrderId && (
                <p className="text-sm text-slate-500">Order #: {wooOrderId}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Complete Your Payment</h1>
          <p className="text-slate-400">Order #{orderId}</p>
        </div>

        {/* Order Summary */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <ShoppingCart className="h-5 w-5 text-emerald-400" />
              Order Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Customer Info */}
            {(order?.customer_name?.firstName || order?.customer_name?.lastName) && (
              <div className="text-sm text-slate-400">
                Customer: {order.customer_name.firstName} {order.customer_name.lastName}
              </div>
            )}
            
            {/* Items */}
            <div className="space-y-2">
              {order?.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-white">
                    {item.name || item.description} × {item.quantity}
                  </span>
                  <span className="text-white">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              
              {order?.custom_items?.map((item, idx) => (
                <div key={`custom-${idx}`} className="flex justify-between text-sm">
                  <span className="text-amber-300">
                    {item.name || item.description} × {item.quantity}
                  </span>
                  <span className="text-amber-400">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              
              {order?.reschedule_fees?.map((fee, idx) => (
                <div key={`fee-${idx}`} className="flex justify-between text-sm">
                  <span className="text-orange-300">
                    {fee.name || fee.description} × {fee.quantity}
                  </span>
                  <span className="text-orange-400">${(fee.price * fee.quantity).toFixed(2)}</span>
                </div>
              ))}
              
              {/* Travel Miles */}
              {order?.fees?.mileage > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-cyan-300">
                    Travel Miles ({order.fees.mileage} mi)
                  </span>
                  <span className="text-cyan-400">
                    ${((order.total - order.items?.reduce((sum, i) => sum + i.price * i.quantity, 0) - 
                       (order.custom_items?.reduce((sum, i) => sum + i.price * i.quantity, 0) || 0) -
                       (order.reschedule_fees?.reduce((sum, f) => sum + f.price * f.quantity, 0) || 0) +
                       (order.discount?.amount || 0))).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
            
            {/* Discount */}
            {order?.discount?.amount > 0 && (
              <div className="flex justify-between text-sm border-t border-slate-700 pt-2">
                <span className="text-purple-400">Discount</span>
                <span className="text-purple-400">-${order.discount.amount.toFixed(2)}</span>
              </div>
            )}
            
            {/* Total */}
            <div className="flex justify-between text-lg font-bold border-t border-slate-700 pt-3">
              <span className="text-white">Total</span>
              <span className="text-emerald-400">${order?.total?.toFixed(2) || '0.00'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Payment Section */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <CreditCard className="h-5 w-5 text-emerald-400" />
              Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}
            
            {paymentProcessing ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                <span className="ml-3 text-slate-400">Processing payment...</span>
              </div>
            ) : stripePromise ? (
              <Elements stripe={stripePromise}>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white text-sm mb-2 block">Card Details</Label>
                    <div className="bg-slate-700 border border-teal-600 rounded-lg p-4">
                      <CardElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                  </div>
                  
                  <PaymentSubmitButton
                    onApprove={onApprove}
                    onError={onError}
                    disabled={paymentProcessing}
                    total={order?.total}
                    orderId={orderId}
                  />
                </div>
              </Elements>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-400">Loading payment form...</span>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Lock className="h-4 w-4" />
            <span>Secure payment powered by Stripe</span>
          </div>
          <p className="text-xs text-slate-600">
            Your card details are encrypted and securely processed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default POSPaymentPage;
